import { Action, Module, VuexModule } from 'vuex-class-modules'
import store from '@/store/index'
import vehicle from '@/services/vehicle'
import split from '@/store/modules/split'
import { VehicleAmenityAlert } from '@/models/dto/Vehicle'
import { SplitFeatureFlag } from '@/utils/enum'
import {
  clearVehicleAmenityAlerts,
  loadVehicleAmenityAlerts,
  mergeAmenityAlerts,
  saveVehicleAmenityAlerts,
} from '@/utils/vehicleAmenityAlerts'

/** Minimum time between amenity-alert refetches. */
export const AMENITY_ALERTS_FETCH_TTL_MS = 5 * 60 * 1000

@Module({ generateMutationSetters: true })
class VehicleAmenityAlertsModule extends VuexModule {
  _pending: VehicleAmenityAlert[] = loadVehicleAmenityAlerts()
  _lastFetchedAt: number | null = null

  get pending(): VehicleAmenityAlert[] {
    return this._pending
  }

  get lastFetchedAt(): number | null {
    return this._lastFetchedAt
  }

  isFetchStale(): boolean {
    if (this._lastFetchedAt == null) {
      return true
    }
    return Date.now() - this._lastFetchedAt >= AMENITY_ALERTS_FETCH_TTL_MS
  }

  @Action
  async saveAmenityAlertsIfEnabled(): Promise<VehicleAmenityAlert[]> {
    const isEnabled = await split.isFeatureEnabled(
      SplitFeatureFlag.AmenitiesExceptions
    )
    if (!isEnabled) {
      return []
    }
    return this.saveAmenityAlerts()
  }

  @Action
  async saveAmenityAlerts(): Promise<VehicleAmenityAlert[]> {
    if (!this.isFetchStale()) {
      return []
    }

    this._lastFetchedAt = Date.now()

    try {
      const { data } = await vehicle.getAmenityAlerts()
      const merged = mergeAmenityAlerts(this._pending, data)

      this._pending = merged
      saveVehicleAmenityAlerts(merged)

      return data
    } catch (error) {
      // Do not let amenity-alert failures bubble into router auth handling.
      console.warn('Error fetching vehicle amenity alerts:', error)
      this._lastFetchedAt = null
      return []
    }
  }

  @Action
  dismissAmenityAlert(alertId: string): void {
    this._pending = this._pending.filter((alert) => alert.id !== alertId)
    saveVehicleAmenityAlerts(this._pending)
  }

  @Action
  reset(): void {
    this._pending = []
    this._lastFetchedAt = null
    clearVehicleAmenityAlerts()
  }
}

export default new VehicleAmenityAlertsModule({
  store,
  name: 'vehicleAmenityAlerts',
})
